import React, { useState } from 'react';
import { IListingData } from '@models/listing';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import { IConfirmation } from '@redux/confirmation/types';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  activateBusinessPartnerLine,
  deactivateBusinessPartnerLine,
} from './queries';
import { isEmpty } from '@utils/validationUtils';
import { toast } from 'react-toastify';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { getError } from '@utils/graph-utils';
import { useAppDispatch } from '@redux/hooks';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import { getBusinessPartnerLinesList } from './queries';
import { IBPGeneralData } from '../../..';
import {
  ActionTarget,
  IEnhancedMenuItem,
  IFilterModel,
} from '@components/table/types';
import { mapToAssignedLinesListingData } from './utils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { IAssignedLinesTableData } from '.';
import { bpAssignedLinesHeaders } from './content';
import LineCreditLimitDrawer from './line-credit-limit-drawer/LineCreditLimitDrawer';
import DefaultCommissionDrawer from './default-commission-drawer/DefaultCommissionDrawer';
import { useResetKey } from '@hooks/useResetKey';

interface IAssignedLinesWidget {
  generalData: IBPGeneralData;
  onSuccess: () => void;
}

const AssignedLinesWidget: React.FC<IAssignedLinesWidget> = ({
  generalData,
  onSuccess,
}) => {
  const [tableKey, refreshTable] = useResetKey('tableKey');
  const dispatch = useAppDispatch();

  const [filterValues, setFilterValues] = useState<IFilterModel>({
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
    descending: false,
    orderBy: 'salesforceManagement_BusinessPartnerVignetteRange_RangeCode',
    searchKey: null,
  });

  const [tableData, setTableData] = useState<
    IListingData<IAssignedLinesTableData>
  >({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [drawer, setDrawer] = useState<{
    selectedIds: string[];
    isOpen: boolean;
    type: 'lineCreditLimit' | 'defaultCommission';
  }>({
    selectedIds: [],
    isOpen: false,
    type: 'lineCreditLimit',
  });

  const [getAssignedLinesLazy, { loading }] = useLazyQuery(
    getBusinessPartnerLinesList
  );
  const [activateBusinessPartnerLineAction] = useMutation(
    activateBusinessPartnerLine
  );
  const [deactivateBusinessPartnerLineAction] = useMutation(
    deactivateBusinessPartnerLine
  );

  const handleSuccess = () => {
    refreshTable(); // refresh local table
    onSuccess(); // refresh the assigned Plans Table
  };

  const activateAssignedLine = (selectedId: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Activate',
      message: `Are you sure you want to activate the selected assigned line?`,
      callback: async () => {
        try {
          const result = await activateBusinessPartnerLineAction({
            variables: {
              entityId: selectedId,
            },
            errorPolicy: 'all',
          });

          if (isEmpty(result.errors)) {
            handleSuccess();
            toast.success(
              <ToastSuccessMessage>
                Assigned line successfully activated. Ensure to update Line
                Credit Limit. Note that activating the assigned line will not
                activate the relevant plans.
              </ToastSuccessMessage>
            );
          } else {
            toast.error(
              <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
            );
          }
        } catch (error) {
          toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
        }
      },
      submitButtonText: 'Submit',
      cancelButtonText: 'Cancel',
    };

    dispatch(OpenConfirmationAction(confirmation));
  };

  const deactivateAssignedLine = (selectedId: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Deactivate',
      message: `Are you sure you want to deactivate the selected assigned line?
      Note that deactivating the assigned line will deactivate all assigned plans.`,
      callback: async () => {
        try {
          const result = await deactivateBusinessPartnerLineAction({
            variables: {
              entityId: selectedId,
            },
            errorPolicy: 'all',
          });

          if (isEmpty(result.errors)) {
            handleSuccess();
            toast.success(
              <ToastSuccessMessage>
                Assigned line successfully deactivated. Note that Credit Limit
                is set to 0 and all relevant plans are deactivated as well.
              </ToastSuccessMessage>
            );
          } else {
            toast.error(
              <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
            );
          }
        } catch (error) {
          toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
        }
      },
      submitButtonText: 'Submit',
      cancelButtonText: 'Cancel',
    };

    dispatch(OpenConfirmationAction(confirmation));
  };

  const getListingData = async (filter = filterValues) => {
    if (!filter) {
      return {};
    }

    return getAssignedLinesLazy({
      variables: {
        pagination: filter.pagination,
        selectedBusinessPartnerID: generalData.businessPartner.id,
        Attribute: filter.orderBy || 'Line_ExternalCode',
        Descending: filter.descending,
        keywordSearch: filter.searchKey,
      },
      errorPolicy: 'all',
    })
      .then(({ data }) => {
        if (data) {
          const tableData = mapToAssignedLinesListingData(data);
          setTableData({
            ...tableData,
            pageNumber: tableData.pageNumber,
            pageSize: tableData.pageSize,
            totalCount: tableData.totalCount,
          });
        }
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      });
  };

  function handlePageChange(filterModel: IFilterModel): Promise<void> {
    return new Promise<void>(() => getListingData(filterModel));
  }

  const actions: IEnhancedMenuItem[] = [
    {
      name: 'setDefaultCommission',
      title: 'Set Default Commission',
      isPrimary: false,
      supportBulk: false,
      target: ActionTarget.Entity,
      displayActionPerRecord: true,
      onClick: (selectedIds: string[]) => {
        setDrawer({
          isOpen: true,
          selectedIds: selectedIds,
          type: 'defaultCommission',
        });
      },
    },
    {
      name: 'updateCreditLimit',
      title: 'Update Credit Limit',
      isPrimary: false,
      supportBulk: true,
      target: ActionTarget.Entity,
      displayActionPerRecord: true,
      onClick: (selectedIds: string[]) => {
        setDrawer({
          isOpen: true,
          selectedIds: selectedIds,
          type: 'lineCreditLimit',
        });
      },
    },
    {
      name: 'activate',
      title: 'Activate',
      isPrimary: false,
      supportBulk: true,
      target: ActionTarget.Entity,
      displayActionPerRecord: true,
      onClick: (selectedIds: string[]) => {
        activateAssignedLine(selectedIds[0]);
      },
    },
    {
      name: 'deactivate',
      title: 'Deactivate',
      isPrimary: false,
      supportBulk: true,
      target: ActionTarget.Entity,
      displayActionPerRecord: true,
      onClick: (selectedIds: string[]) => {
        deactivateAssignedLine(selectedIds[0]);
      },
    },
  ];

  return (
    <>
      <ListingWidget
        key={tableKey}
        title={''}
        inlineTitle="Assigned Lines"
        name="assignedLines"
        orderByAscendingByDefault
        defaultOrderByColumn="name"
        inline={true}
        data={tableData}
        tableSettings={{
          headers: bpAssignedLinesHeaders,
        }}
        usePagination
        dropdownActionsNames={[
          'setDefaultCommission',
          'updateCreditLimit',
          'activate',
          'deactivate',
        ]}
        loading={loading}
        actions={
          generalData.userIsAdmin && generalData.businessPartner.isActive
            ? actions
            : []
        }
        onPageChange={(filterModel) => {
          setFilterValues(filterModel);
          return handlePageChange(filterModel);
        }}
      />
      {drawer.isOpen && drawer.type === 'lineCreditLimit' && (
        <LineCreditLimitDrawer
          open={drawer.isOpen && drawer.type === 'lineCreditLimit'}
          businessPartnerId={generalData.businessPartner.id}
          businessPartnerLinesId={drawer.selectedIds}
          onClose={() =>
            setDrawer({
              isOpen: false,
              selectedIds: [],
              type: 'lineCreditLimit',
            })
          }
          onSuccess={handleSuccess}
        />
      )}
      {drawer.isOpen && drawer.type === 'defaultCommission' && (
        <DefaultCommissionDrawer
          open={drawer.isOpen && drawer.type === 'defaultCommission'}
          businessPartnerId={generalData.businessPartner.id}
          onClose={() =>
            setDrawer({
              isOpen: false,
              selectedIds: [],
              type: 'defaultCommission',
            })
          }
          onSuccess={handleSuccess}
          assignedLineId={drawer.selectedIds[0]}
        />
      )}
    </>
  );
};

export default AssignedLinesWidget;
